/** Named companions belong to a particular host, including while it fights in a city. */
import { HERO_CLASSES, levelForXp } from '../../data/heroes.ts';
import { Team } from '../types.ts';
import { HERO_REVIVE_SECONDS } from '../systems/hero.ts';
import {
  cloneCompanion, companionUnit, createCompanion, fieldCompanionAt,
  COMPANION_RECRUIT_GOLD, MAX_COMPANIONS,
} from '../systems/companions.ts';
import { findCompany, invalidateCompanyIndex, type World } from '../world.ts';
import { recount } from '../systems/towers.ts';
import { findArmy, nationFactionId } from './armies.ts';
import { logLine } from './state.ts';
import { ArmyStance, BattleKind, type Army, type CampaignState } from './types.ts';

export { COMPANION_RECRUIT_GOLD, MAX_COMPANIONS } from '../systems/companions.ts';

function safeHolding(c: CampaignState, a: Army): boolean {
  const p = c.provinces[a.at];
  return !!p && p.owner === a.owner && p.tier >= 1 && p.siegeBy < 0 &&
    a.moveTo < 0 && a.aboard < 0 && a.stance === ArmyStance.Field;
}

/** The same refusal is used by the command authority and the recruitment panel. */
export function recruitHeroError(c: CampaignState, army: Army | undefined, classId: string, nation = c.playerNation): string {
  if (!army || army.owner !== nation) return 'Choose one of your armies.';
  if (c.over || !c.nations[nation]?.alive) return 'This campaign is over.';
  if (c.pending || c.pendingEvent) return 'Resolve the pending encounter first.';
  if (!safeHolding(c, army) || army.regroup > 0) return 'Recruit while standing in an owned, unbesieged holding.';
  const cls = HERO_CLASSES.find((h) => h.id === classId);
  if (!cls) return 'No such hero class.';
  if (cls.factions.length && !cls.factions.includes(nationFactionId(c, nation))) return 'This hero serves another people.';
  if (army.heroes.some((h) => h.classId === classId)) return 'This army already has a hero of this class.';
  if (army.heroes.length >= MAX_COMPANIONS) return `An army can support ${MAX_COMPANIONS} companions.`;
  if (c.nations[nation].treasury.gold < COMPANION_RECRUIT_GOLD) return `Needs ${COMPANION_RECRUIT_GOLD} gold.`;
  return '';
}

export function recruitArmyHero(w: World, armyId: number, classId: string, nation = w.campaign?.playerNation ?? -1): boolean {
  const c = w.campaign;
  if (!c) return false;
  const a = findArmy(c, armyId);
  if (recruitHeroError(c, a, classId, nation)) return false;
  // Old saves may have been written before the realm's next-id counter knew
  // about attached companions. Never mint the identity of an existing hero.
  let id = w.nextCompanionId;
  for (const army of c.armies) for (const h of army.heroes) id = Math.max(id, h.id + 1);
  for (const h of w.companions) id = Math.max(id, h.id + 1);
  const hero = createCompanion(id, classId, w.config.seed, a!.id);
  a!.heroes.push(hero);
  w.nextCompanionId = id + 1;
  c.nations[nation].treasury.gold -= COMPANION_RECRUIT_GOLD;
  logLine(c, `${hero.name}, ${HERO_CLASSES.find((h) => h.id === classId)!.name}, joins ${a!.name}.`);
  return true;
}

/** Includes all hosts sheltered in the attacked city, never nearby field armies. */
function participants(c: CampaignState): { army: Army; attacking: boolean }[] {
  const p = c.pending;
  if (!p) return [];
  return c.armies.flatMap((army) => {
    if (army.id === p.attackerArmy && army.owner === p.attackerNation) return [{ army, attacking: true }];
    if (army.owner !== p.defenderNation) return [];
    if (army.id === p.defenderArmy || (p.kind === BattleKind.Province && army.sheltered &&
      army.at === p.province && army.moveTo < 0 && army.aboard < 0)) return [{ army, attacking: false }];
    return [];
  }).sort((a, b) => Number(b.attacking) - Number(a.attacking) || a.army.id - b.army.id);
}

function deployPoint(w: World, team: number, offset: number): { x: number; y: number } | null {
  const b = w.battle;
  let x = w.map.hearthX + 0.5, y = w.map.hearthY + 4.5;
  if (b && team === Team.Player) {
    x = (b.deployZone.x0 + b.deployZone.x1) / 2;
    y = b.deployZone.y1 - 1.5;
  } else if (b) {
    const army = b.enemy;
    const companies = army.companies.map((id) => findCompany(w, id)).filter((v) => v !== undefined);
    if (companies.length) {
      x = companies.reduce((sum, v) => sum + v.ax, 0) / companies.length;
      y = companies.reduce((sum, v) => sum + v.ay, 0) / companies.length - 2;
    }
  }
  x += (offset % 5 - 2) * 1.5;
  y += Math.floor(offset / 5) * 1.5;
  const tx = Math.floor(x), ty = Math.floor(y);
  // A stored city can have a workshop in the muster square. Search locally
  // without crossing its wall, rather than placing a hero inside a building.
  for (let r = 0; r <= 6; r++) for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
    if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
    const px = tx + dx, py = ty + dy;
    if (!Number.isFinite(w.map.moveCost(px, py))) continue;
    let clear = true;
    for (let step = 0, count = Math.max(Math.abs(dx), Math.abs(dy)); step <= count; step++) {
      const kx = tx + Math.round(dx * step / Math.max(1, count));
      const ky = ty + Math.round(dy * step / Math.max(1, count));
      if (w.map.inBounds(kx, ky) && w.map.wall[w.map.idx(kx, ky)]) { clear = false; break; }
    }
    if (clear) return { x: px + 0.5, y: py + 0.5 };
  }
  return null;
}

/** Call after the field/city muster, while the realm still has its pending battle. */
export function carryArmyHeroesInto(field: World, realm: World): number {
  const c = realm.campaign, p = c?.pending;
  if (!c || !p || field === realm) return 0;
  if (c.playerNation !== p.attackerNation && c.playerNation !== p.defenderNation) return 0;
  const pvp = c.humanNations.includes(p.attackerNation) && c.humanNations.includes(p.defenderNation);
  let count = 0;
  for (const { army, attacking } of participants(c)) {
    // Both clients must build the same shared battlefield. Ordinary campaign
    // enemies do not recruit companions, so only the player's host is copied.
    if (!pvp && army.owner !== c.playerNation) continue;
    const team = pvp ? (attacking ? Team.Player : Team.Invader) : Team.Player;
    for (const source of army.heroes) {
      if (source.fallen || field.companions.some((h) => h.armyId === army.id && h.classId === source.classId)) continue;
      const point = deployPoint(field, team, count);
      if (!point) continue;
      const h = cloneCompanion(source);
      h.unitH = -1; h.companyId = -1;
      // A city may already have a resident companion with the same numeric id.
      // Army + class identify the origin; its strategic id never changes.
      if (field.companions.some((v) => v.id === h.id)) {
        h.id = Math.max(field.nextCompanionId, ...field.companions.map((v) => v.id + 1));
      }
      if (!fieldCompanionAt(field, h, point.x, point.y, team)) continue;
      const battleArmy = field.battle && (team === Team.Player ? field.battle.player : field.battle.enemy);
      if (battleArmy && !battleArmy.companies.includes(h.companyId)) battleArmy.companies.push(h.companyId);
      count++;
    }
  }
  recount(field);
  return count;
}

/** Copy individual results before the strategic verdict, then remove tactical copies. */
export function takeArmyHeroesHome(field: World, realm: World): number {
  const c = realm.campaign;
  if (!c || !c.pending || field === realm) return 0;
  const origins = new Map(participants(c).map(({ army }) => [army.id, army]));
  const copied = new Set<number>(), companies = new Set<number>();
  for (const h of field.companions) {
    const a = origins.get(h.armyId), original = a?.heroes.find((v) => v.classId === h.classId);
    if (!original) continue;
    const i = companionUnit(field, h);
    const fell = h.fallen || i < 0 || field.units.hp[i] <= 0;
    Object.assign(original, cloneCompanion(h), {
      id: original.id, armyId: a!.id, unitH: -1, companyId: -1,
      fallen: fell, reviveT: fell ? (h.fallen ? h.reviveT : HERO_REVIVE_SECONDS) : 0,
    });
    copied.add(h.id);
    if (h.companyId >= 0) companies.add(h.companyId);
    if (i >= 0) field.units.kill(i);
  }
  if (!copied.size) return 0;
  field.companions = field.companions.filter((h) => !copied.has(h.id));
  field.companies = field.companies.filter((a) => !companies.has(a.id));
  if (field.battle) for (const a of [field.battle.player, field.battle.enemy]) {
    a.companies = a.companies.filter((id) => !companies.has(id));
  }
  invalidateCompanyIndex(field);
  recount(field);
  return copied.size;
}

/** No model-fraction dice on named heroes: an abstract defeat leaves them wounded. */
export function resolveArmyHeroesAbstractly(c: CampaignState, attackerWon: boolean): void {
  for (const { army, attacking } of participants(c)) for (const h of army.heroes) {
    if (h.fallen) continue;
    if (attacking === attackerWon) {
      h.xp += 60; h.level = levelForXp(h.xp);
    } else {
      h.fallen = true; h.reviveT = HERO_REVIVE_SECONDS;
    }
  }
}

/** The realm pauses during battle; recovery needs the safety of a friendly holding. */
export function stepArmyHeroes(c: CampaignState, dt: number): void {
  if (c.pending || c.pendingEvent || c.over) return;
  for (const a of c.armies) for (const h of a.heroes) {
    for (const key of Object.keys(h.cooldowns)) h.cooldowns[key] = Math.max(0, h.cooldowns[key] - dt);
    if (!h.fallen || !safeHolding(c, a)) continue;
    h.reviveT = Math.max(0, h.reviveT - dt);
    if (h.reviveT <= 0) {
      h.fallen = false;
      logLine(c, `${h.name} has recovered and rejoins ${a.name}.`);
    }
  }
}
